// Primavera P6 relationship (dependency) extraction.
//
// RDrive stores task dependencies as `prerequisiteTaskIds` — a flat list of
// task ids with one fixed meaning: the prerequisite must finish before the
// task starts (finish-to-start), with no lag. There is no relationship-type or
// lag field in the API, so a P6 relationship can only be represented
// faithfully when it is exactly that. Every other edge (Start to Start,
// Finish to Finish, Start to Finish, or any edge with a non-zero lag) is
// dropped rather than approximated, and counted in the returned summary so
// the user can see precisely what was not imported.

import { childText } from '@/utils/XmlDomUtils';
import { forEachChunked } from '@/utils/mainThreadScheduler';

const FINISH_TO_START = 'Finish to Start';

/**
 * Extract the P6 <Relationship> elements RDrive can represent.
 *
 * @param {Document} xmlDoc - Parsed P6 XML export
 * @param {Map<string, string>} idMapping - P6 ObjectId -> new RDrive task id
 *   (as built by XMLTaskParser while parsing WBS nodes and activities)
 * @returns {{
 *   prerequisitesByTaskId: Map<string, Set<string>>,
 *   summary: {
 *     total: number,
 *     imported: number,
 *     droppedByType: Object,
 *     droppedWithLag: number,
 *     crossProject: number,
 *     unresolved: number,
 *     duplicates: number
 *   }
 * }} prerequisitesByTaskId maps a successor task id to its predecessors' task
 *   ids. The summary buckets are mutually exclusive and add up to `total`:
 *   `droppedByType` counts non finish-to-start edges by their P6 type name,
 *   `droppedWithLag` counts finish-to-start edges with a non-zero lag/lead,
 *   `crossProject` counts edges into another P6 project, `unresolved` counts
 *   edges whose endpoints are not among the parsed activities (or that
 *   self-reference), and `duplicates` counts repeated predecessor/successor
 *   pairs.
 */
export async function parseP6Relationships(xmlDoc, idMapping, { onProgress } = {}) {
  const summary = {
    total: 0,
    imported: 0,
    droppedByType: {},
    droppedWithLag: 0,
    crossProject: 0,
    unresolved: 0,
    duplicates: 0
  };
  const prerequisitesByTaskId = new Map();

  // Chunked so a large export's relationship list can't pin the main thread
  // (counts are typically on the order of the activity count).
  const nodes = Array.from(xmlDoc.getElementsByTagName('Relationship'));
  await forEachChunked(nodes, node => {
    summary.total += 1;

    // Relationships into another project reference activities this file does
    // not contain; categorise them before the endpoint lookup so they are not
    // reported as merely unresolved.
    const predecessorProject = childText(node, 'PredecessorProjectObjectId');
    const successorProject = childText(node, 'SuccessorProjectObjectId');
    if (predecessorProject && successorProject && predecessorProject !== successorProject) {
      summary.crossProject += 1;
      return;
    }

    const predecessorId = idMapping.get(childText(node, 'PredecessorActivityObjectId'));
    const successorId = idMapping.get(childText(node, 'SuccessorActivityObjectId'));
    if (!predecessorId || !successorId || predecessorId === successorId) {
      summary.unresolved += 1;
      return;
    }

    const type = childText(node, 'Type') || 'Unknown';
    if (type !== FINISH_TO_START) {
      summary.droppedByType[type] = (summary.droppedByType[type] || 0) + 1;
      return;
    }

    // P6 lags are hours (a negative value is a lead). A missing element means
    // zero. An edge with a lag would claim a different constraint than the
    // one RDrive stores, so it is dropped instead of flattened to zero.
    const lag = parseFloat(childText(node, 'Lag'));
    if (Number.isFinite(lag) && lag !== 0) {
      summary.droppedWithLag += 1;
      return;
    }

    let prerequisites = prerequisitesByTaskId.get(successorId);
    if (!prerequisites) {
      prerequisites = new Set();
      prerequisitesByTaskId.set(successorId, prerequisites);
    }
    if (prerequisites.has(predecessorId)) {
      summary.duplicates += 1;
      return;
    }
    prerequisites.add(predecessorId);
    summary.imported += 1;
  }, {
    onProgress: onProgress
      ? (done, total) => onProgress({ stage: 'relationships', done, total })
      : undefined
  });

  return { prerequisitesByTaskId, summary };
}
